import { AnonymousUser, Twin, User } from 'api/model'
import { useActiveOrganization } from './use-organization'
import { useCallback, useLayoutEffect, useState } from 'react'
import { twinsGetMultiple, twinsGetSharedWith, usersTwinsGet } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { anonymousTwinAtom } from 'state/anonymous-twin'
import { twinsAtom } from 'state/twins'
import { useRecoilState } from 'recoil'
import { twinsGetSharedTwin } from 'api/auth_endpoints/authFromFileSpecWithTransformer'
import { anonymousUserAtom } from 'state'
import { LOCAL_STORAGE } from 'app-constants'
import { myTwinAtom } from 'state/my-twin'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'

function useFetchMyTwin(userId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [myTwin, setMyTwin] = useRecoilState<Twin | null>(myTwinAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetchMyTwin = useCallback(async () => {
    if (!activeOrganization || !userId) {
      return
    }

    setIsLoading(true)

    try {
      const response = await usersTwinsGet(activeOrganization, userId)
      setMyTwin(response.data)
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, userId, setMyTwin])

  useLayoutEffect(() => {
    refetchMyTwin()
  }, [refetchMyTwin])

  return {
    isLoading,
    myTwin,
    refetchMyTwin,
    error,
  }
}

function useFetchUsersMyTwinIsSharedWith(twinId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [usersMyTwinIsSharedWith, setUsersMyTwinIsSharedWith] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!activeOrganization || !twinId) {
        return
      }

      setIsLoading(true)

      try {
        const response = await twinsGetSharedWith(activeOrganization, twinId)
        setUsersMyTwinIsSharedWith(response.data)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeOrganization, setUsersMyTwinIsSharedWith, twinId])

  return {
    isLoading,
    usersMyTwinIsSharedWith,
    error,
    setUsersMyTwinIsSharedWith,
  }
}

function useFetchTwins() {
  const [activeOrganization] = useActiveOrganization()
  const [twins, setTwins] = useRecoilState<Twin[]>(twinsAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!activeOrganization) {
        return
      }

      setIsLoading(true)

      try {
        const response = await twinsGetMultiple(activeOrganization)
        setTwins(response.data)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    if (twins.length === 0) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganization, setTwins])

  return {
    isLoading,
    twins,
    error,
    setTwins,
  }
}

function useFetchAnonymousTwin(shortUrl: string) {
  const [anonymousTwin, setAnonymousTwin] = useRecoilState<Twin | null>(anonymousTwinAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  useLayoutEffect(() => {
    async function fetchData() {
      if (!shortUrl) {
        return
      }

      setIsLoading(true)

      try {
        const response = await twinsGetSharedTwin(shortUrl)
        setAnonymousTwin(response.data)
      } catch (error: unknown) {
        setError(`${error}`)
        if (axios.isAxiosError(error) && error.response?.status === 403) {
          navigate('/twin-not-found')
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [shortUrl, setAnonymousTwin, navigate])

  return {
    isLoading,
    anonymousTwin,
    error,
  }
}

function useFetchAnonymousUser() {
  const [, set] = useRecoilState(anonymousUserAtom)

  const setAnonymousUser = useCallback(
    (anonymousUser: AnonymousUser | null) => {
      if (anonymousUser) {
        localStorage.setItem(LOCAL_STORAGE.ANONYMOUS_USER, JSON.stringify(anonymousUser))
      } else {
        localStorage.removeItem(LOCAL_STORAGE.ANONYMOUS_USER)
      }
      set(anonymousUser)
    },
    [set]
  )

  const getAnonymousUser = (): AnonymousUser | null => {
    const anonymousUser = localStorage.getItem(LOCAL_STORAGE.ANONYMOUS_USER)
    if (anonymousUser) {
      return JSON.parse(anonymousUser)
    }
    return null
  }
  return [getAnonymousUser, setAnonymousUser] as const
}

export { useFetchTwins, useFetchUsersMyTwinIsSharedWith, useFetchMyTwin, useFetchAnonymousTwin, useFetchAnonymousUser }
